import dagre from 'dagre';
import {
	EdgeMarker,
	MarkerType,
	Node,
	Position,
	getIncomers,
	getOutgoers,
} from 'reactflow';
import { GraphLayoutDirection } from 'src/constants/selfServe/executionViewConstants';
import {
	Dependancy,
	TaskDefinition,
	ValidationErrors,
} from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import CustomNodeTypeHOC from '../customNode/customNode';
import { ReactFlowNodeData } from './updateWFDefinitionCanvas.interface';
import {
	ReactFlowHelpNodeIDList,
	ReactFlowNodeShape,
	ReactFlowNodeSize,
	ReactFlowNodeStatus,
	ReactFlowNodeType,
	isTerminalTaskId,
} from '../customNode/customNodeEnums';
import { defaultTaskDefinition } from 'src/stores/taskDefinitionStore/taskDefinitionStore';

const nodeWidth = 200;
const nodeHeight = 50;

const TERMINAL_NODE_START_POS_HORIZONTAL = { x: 50, y: 100 };
const TERMINAL_NODE_END_POS_HORIZONTAL = { x: 300, y: 100 };
const TERMINAL_NODE_START_POS_VERTICAL = { x: 100, y: 50 };
const TERMINAL_NODE_END_POS_VERTICAL = { x: 100, y: 200 };

export const defaultEdgeOptions = {
	type: 'step',
	style: { strokeWidth: 2, stroke: '#000' },
	markerEnd: MarkerType.ArrowClosed,
};

export const getLayoutedElements = (
	nodes,
	edges,
	direction = GraphLayoutDirection.TopToBottom
) => {
	const isHorizontal = direction === GraphLayoutDirection.LeftToRight;

	const hasOnlyTerminalNodes =
		nodes.length === 2 && nodes.every((node) => isStartOrEndNode(node));

	if (hasOnlyTerminalNodes) {
		nodes.forEach((node) => {
			node.targetPosition = isHorizontal ? Position.Left : Position.Top;
			node.sourcePosition = isHorizontal ? Position.Right : Position.Bottom;

			if (node.id === ReactFlowHelpNodeIDList.START) {
				node.position = isHorizontal ? TERMINAL_NODE_START_POS_HORIZONTAL : TERMINAL_NODE_START_POS_VERTICAL;
			} else {
				node.position = isHorizontal ? TERMINAL_NODE_END_POS_HORIZONTAL : TERMINAL_NODE_END_POS_VERTICAL;
			}

			node.data.nodeAdditionalProps.alignment = direction;
		});

		return { nodes, edges };
	}

	const dagreGraph = new dagre.graphlib.Graph();

	dagreGraph.setDefaultEdgeLabel(() => ({}));

	const graphSettings = {
		rankdir: direction,
		acyclicer: 'greedy',
		ranker: 'tight-tree',
		ranksep: 150,
 		nodesep: 100,
	};

	dagreGraph.setGraph(graphSettings);

	nodes.forEach((node) => {
		dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
	});

	edges.forEach((edge) => {
		dagreGraph.setEdge(edge.source, edge.target);
	});

	dagre.layout(dagreGraph);

	nodes.forEach((node) => {
		const nodeWithPosition = dagreGraph.node(node.id);

		node.targetPosition = isHorizontal ? Position.Left : Position.Top;
		node.sourcePosition = isHorizontal ? Position.Right : Position.Bottom;

		// We are shifting the dagre node position (anchor=center center) to the top left
		// so it matches the React Flow node anchor point (top left).
		node.position = {
			x: nodeWithPosition.x - nodeWidth / 2,
			y: nodeWithPosition.y - nodeHeight / 2,
		};

		node.data.nodeAdditionalProps.alignment = direction;

		return node;
	});

	return { nodes, edges };
};

export const generateGraphForDisplay = (
	taskDefinitions: TaskDefinition[],
	dependencies: Dependancy[],
	showHotspot = false
) => {
	const startNode = generateTerminalNode(
		ReactFlowNodeType.UNI_DIRECTIONAL,
		false,
		ReactFlowHelpNodeIDList.START,
		50,
		showHotspot
	);
	const endNode = generateTerminalNode(
		ReactFlowNodeType.UNI_DIRECTIONAL,
		true,
		ReactFlowHelpNodeIDList.END,
		(taskDefinitions.length + 2) * 150,
		false
	);
	let initialNodes: Node<ReactFlowNodeData>[] =
		taskDefinitions.map(generateTaskNode);
	const initialEdges = dependencies.map(generateEdge);

	initialNodes = [...initialNodes, startNode, endNode];

	const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
		initialNodes,
		initialEdges
	);

	return { layoutedNodes, layoutedEdges };
};

const generateTaskNode = (
	taskDefinition: TaskDefinition
): Node<ReactFlowNodeData> => {
	return {
		id: taskDefinition.taskId,
		type: ReactFlowNodeType.BI_DIRECTIONAL,
		position: { x: 150, y: 600 },
		data: {
			taskDefinition: { ...taskDefinition },
			nodeAdditionalProps: {
				alignment: GraphLayoutDirection.TopToBottom,
				name: taskDefinition.taskName,
				status: ReactFlowNodeStatus.WORKFLOW_REVIEW,
				isClickable: true,
				isIncoming: true,
			},
		},
	};
};

const generateTerminalNode = (
	nodeType,
	incoming,
	id,
	positionY,
	showHotspot = false
): Node<ReactFlowNodeData> => {
	return {
		id,
		type: nodeType,
		position: { x: 150, y: positionY },
		data: {
			taskDefinition: {
				...defaultTaskDefinition,
				taskId: id as string,
			},
			nodeAdditionalProps: {
				alignment: GraphLayoutDirection.TopToBottom,
				name: id,
				isIncoming: incoming,
				status: ReactFlowNodeStatus.DISABLED,
				shape: ReactFlowNodeShape.ROUND,
				size: ReactFlowNodeSize.SMALL,
				showHotspot,
			},
		},
	};
};

const generateEdge = (dependency: Dependancy) => {
	return {
		id: `${dependency.start}-${dependency.end}`,
		source: dependency.start,
		target: dependency.end,
		...defaultEdgeStyles,
	};
};

export const defaultEdgeStyles = {
	type: 'default',
	style: { strokeWidth: 2, stroke: '#000', float: true },
	markerEnd: {
		type: MarkerType.ArrowClosed,
		strokeWidth: 1,
		color: '#000',
	},
};

export const ArrowEnd: EdgeMarker = {
	type: MarkerType.ArrowClosed,
	color: '#000',
	width: 20,
	height: 20,
};

export function getDisconnectedNodesErrors(nodes, edges) {
	const validationErrors: ValidationErrors = {
		errorMessages: [],
		errorNodeIDList: [],
	};

	nodes.forEach((node) => {
		const incomers = getOutgoers(node, nodes, edges);
		const outComers = getIncomers(node, nodes, edges);

		if (!isStartOrEndNode(node) && !(incomers.length && outComers.length)) {
			validationErrors.errorMessages.push(
				`${node.data.taskDefinition.taskName} is disconnected from the workflow`
			);
			validationErrors.errorNodeIDList.push(node.id);
		}
	});

	return validationErrors;
}

export function getCycleinWFGraphError(
	startNode,
	nodes,
	edges,
	visitedNodes: string[] = [],
	validationErrors: ValidationErrors = {
		errorMessages: [],
		errorNodeIDList: [],
	}
) {
	const { errorMessages, errorNodeIDList } = validationErrors;

	if (errorMessages.length) {
		return validationErrors;
	}

	const outgoers = getOutgoers(startNode, nodes, edges);

	visitedNodes.push(startNode.id);
	outgoers.forEach((node) => {
		if (visitedNodes.includes(node.id)) {
			errorMessages.push(
				`cyclic reference found in graph for Task name:  ${node.data.taskDefinition.taskName}`
			);
			errorNodeIDList.push(node.id);
		}

		getCycleinWFGraphError(node, nodes, edges, visitedNodes, validationErrors);
	});
	visitedNodes.pop();

	return validationErrors;
}

export function getGraphValidationErrors(
	startNode,
	nodes,
	edges
): ValidationErrors {
	const {
		errorMessages: cycleInGraphErrorMessages,
		errorNodeIDList: cycleInGraphErrorNodes,
	} = getCycleinWFGraphError(startNode, nodes, edges);
	const {
		errorMessages: disconnectedNodesErrorMessages,
		errorNodeIDList: disconnectedNodesErrorNodes,
	} = getDisconnectedNodesErrors(nodes, edges);

	return {
		errorMessages: [
			...cycleInGraphErrorMessages,
			...disconnectedNodesErrorMessages,
		],
		errorNodeIDList: [
			...cycleInGraphErrorNodes,
			...disconnectedNodesErrorNodes,
		],
	};
}

export const scrollWorkflowDesginer = (top: number) => {
	const scrollingElement = document.getElementsByClassName(
		'awsui-app-layout__content'
	);

	scrollingElement[0]?.scrollBy({
		top,
		left: 0,
		behavior: 'smooth',
	});
};

export function isStartOrEndNode(node) {
	return isTerminalTaskId(node.id);
}

export const nodeTypes = {
	tutorial: CustomNodeTypeHOC(ReactFlowNodeType.TUTORIAL),
	uniDirectional: CustomNodeTypeHOC(ReactFlowNodeType.UNI_DIRECTIONAL),
	biDirectional: CustomNodeTypeHOC(ReactFlowNodeType.BI_DIRECTIONAL),
};
